import React from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Paper, Typography, Grid, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AppShell from '../../../components/layout/AppShell'
import PageHeader from '../../../components/ui/PageHeader'

Show.layout = (page) => <AppShell>{page}</AppShell>

const TYPE_LABELS = { Person: 'Person', Company: 'Unternehmen' }
const ROLE_LABELS = { owner: 'Eigentümer', tenant: 'Mieter', guarantor: 'Bürge', representative: 'Vertreter' }
const CONTRACT_TYPE_LABELS = { OwnershipContract: 'Eigentum', LeaseContract: 'Miete', Vacancy: 'Leerstand' }
const SALUTATION_LABELS = { herr: 'Herr', frau: 'Frau' }
const TITLE_LABELS = { dr: 'Dr.', prof: 'Prof.', prof_dr: 'Prof. Dr.' }

function DetailRow({ label, value }) {
  if (!value) return null
  return (
    <Grid size={{ xs: 12 }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  )
}

export default function Show({ contact, contracts = [], bank_accounts = [] }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/contacts`
  const isPerson = contact.type === 'Person'

  const handleDelete = () => {
    router.delete(`${basePath}/${contact.id}`)
  }

  return (
    <Box>
      <PageHeader
        title={contact.display_name}
        breadcrumbs={[
          { label: 'Dashboard', href: `/m/${tenant.slug}` },
          { label: 'Kontakte', href: basePath },
          { label: contact.display_name },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => router.visit(`${basePath}/${contact.id}/edit`)}
              data-testid="edit-contact-btn"
            >
              Bearbeiten
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              data-testid="delete-contact-btn"
            >
              Löschen
            </Button>
          </Box>
        }
      />

      {/* Contact info */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Chip
            size="small"
            label={TYPE_LABELS[contact.type] || contact.type}
            color={isPerson ? 'primary' : 'secondary'}
            variant="outlined"
          />
          <Chip
            size="small"
            label={contact.active ? 'Aktiv' : 'Inaktiv'}
            color={contact.active ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>

        <Grid container spacing={1}>
          {isPerson && (
            <>
              <DetailRow label="Anrede" value={SALUTATION_LABELS[contact.salutation]} />
              <DetailRow label="Titel" value={TITLE_LABELS[contact.title]} />
              <DetailRow label="Vorname" value={contact.name2} />
              <DetailRow label="Nachname" value={contact.name1} />
            </>
          )}
          {!isPerson && (
            <>
              <DetailRow label="Firmenname" value={contact.name1} />
              <DetailRow label="Abteilung" value={contact.name2} />
              <DetailRow label="Rechtsform" value={contact.legal_form} />
              <DetailRow label="Steuernummer" value={contact.tax_number} />
              <DetailRow label="USt-IdNr." value={contact.vat_id} />
              <DetailRow label="Handelsregisternummer" value={contact.trade_register_number} />
            </>
          )}
          <DetailRow label="E-Mail" value={contact.email} />
          <DetailRow label="Telefon" value={contact.phone} />
          <DetailRow label="Telefon (geschäftlich)" value={contact.phone_business} />
          <DetailRow label="Mobiltelefon" value={contact.phone_mobile} />
          <DetailRow label="Fax" value={contact.fax} />
          <DetailRow label="Webseite" value={contact.website} />
          <DetailRow label="Briefanrede" value={contact.letter_salutation} />
          <DetailRow label="Alternativempfänger" value={contact.alternative_recipient} />
        </Grid>
      </Paper>

      {/* Address */}
      {contact.address && contact.address.street && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Adresse</Typography>
          <Typography variant="body2">
            {contact.address.street} {contact.address.house_number}
          </Typography>
          <Typography variant="body2">
            {contact.address.postal_code} {contact.address.city}
          </Typography>
          {contact.address.country && contact.address.country !== 'DE' && (
            <Typography variant="body2">{contact.address.country}</Typography>
          )}
        </Paper>
      )}

      {/* Contracts */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Verträge
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Vertrag Nr.</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Rolle</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Beginn</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Ende</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contracts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">Keine Verträge vorhanden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {contracts.map((cc) => (
              <TableRow
                key={cc.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => router.visit(`/m/${tenant.slug}/properties/${cc.property_id}/contracts/${cc.id}`)}
                data-testid={`contract-row-${cc.id}`}
              >
                <TableCell><Typography variant="body2" fontWeight={500}>{cc.number || '–'}</Typography></TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={CONTRACT_TYPE_LABELS[cc.type] || cc.type}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </TableCell>
                <TableCell><Typography variant="body2">{cc.unit_number}</Typography></TableCell>
                <TableCell><Typography variant="body2">{ROLE_LABELS[cc.role] || cc.role}</Typography></TableCell>
                <TableCell><Typography variant="body2">{cc.start_date || '–'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{cc.end_date || '–'}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bank accounts */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Bankkonten
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>IBAN</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kontoinhaber</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Bank</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>BIC</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bank_accounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">Keine Bankkonten vorhanden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {bank_accounts.map((ba) => (
              <TableRow key={ba.id} data-testid={`bank-account-row-${ba.id}`}>
                <TableCell><Typography variant="body2" fontWeight={500}>{ba.iban}</Typography></TableCell>
                <TableCell><Typography variant="body2">{ba.account_holder || '–'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{ba.bank_name || '–'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{ba.bic || '–'}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
